import * as Ops from "./ops/index.js";
import * as Reactives from './reactives/index.js';
import { resolveSource } from "./resolve-source.js";
import { toArray, toArrayOrThrow } from "./to-array.js";
import { messageHasValue } from "./util.js";
import { mapObjectShallow } from '@ixfx/core/records';
import * as Enacts from './sinks/index.js';
/**
 * Wrap a reactive source to allow for chained
 * function calls.
 *
 * Example:
 * For every `pointerup` event on the body, chunk the events over
 * periods of 200ms, get the number of events in that period,
 * and print it out.
 *
 * eg. detecting single or double-clicks
 * ```js
 * wrap(Rx.fromEvent<{ x: number, y: number }>(document.body, `pointerup`))
 *  .chunk({ elapsed: 200 })
 *  .transform(v => v.length)
 *  .onValue(v => { console.log(v) });
 * ```
 * @param source
 * @returns
 */
export function wrap(source) {
    return {
        source: resolveSource(source),
        enacts: {
            setHtmlText: (options) => {
                return Enacts.setHtmlText(source, options);
            },
        },
        annotate: (transformer) => {
            const a = Ops.annotate(source, transformer);
            return wrap(a);
        },
        annotateWithOp: (op) => {
            const a = Ops.annotateWithOp(source, op);
            return wrap(a);
        },
        chunk: (options) => {
            const w = wrap(Ops.chunk(source, options));
            return w;
        },
        // debounce: (options: Partial<DebounceOptions> = {}) => {
        //   return wrap(Ops.debounce<TIn>(source, options));
        // },
        debounce: (options = {}) => {
            return wrap(Reactives.debounce(source, options));
        },
        field: (fieldName, options = {}) => {
            // Ops.field requires TIn extends object
            // Would be good if `wrap` returns different versions depending on TIn, so .field
            // would not be present at all if we had Reactive<number>, for example
            // @ts-expect-error
            const f = Ops.field(source, fieldName, options);
            return wrap(f);
        },
        filter: (predicate, options) => {
            return wrap(Ops.filter(source, predicate, options));
        },
        combineLatestToArray: (sources, options = {}) => {
            const srcs = [source, ...sources];
            return wrap(Ops.combineLatestToArray(srcs, options));
        },
        combineLatestToObject: (sources, options) => {
            const name = options.name ?? `source`;
            const o = { ...sources };
            o[name] = source;
            return wrap(Ops.combineLatestToObject(o, options));
        },
        min: (options = {}) => {
            return wrap(Ops.min(source, options));
        },
        max: (options = {}) => {
            return wrap(Ops.max(source, options));
        },
        average: (options = {}) => {
            return wrap(Ops.average(source, options));
        },
        sum: (options = {}) => {
            return wrap(Ops.sum(source, options));
        },
        tally: (options = {}) => {
            return wrap(Ops.tally(source, options));
        },
        split: (options = {}) => {
            const streams = Ops.split(source, options).map(v => wrap(v));
            return streams;
        },
        splitLabelled: (...labels) => {
            const l = Ops.splitLabelled(source, labels);
            const m = mapObjectShallow(l, args => wrap(args.value));
            return m;
        },
        switcher: (cases, options = {}) => {
            const s = Ops.switcher(source, cases, options);
            const m = mapObjectShallow(s, args => wrap(args.value));
            return m;
        },
        syncToArray: (additionalSources, options = {}) => {
            const unwrapped = [source, ...additionalSources].map(v => resolveSource(v));
            const x = Ops.syncToArray(unwrapped, options);
            return wrap(x); //synchronise<TIn>([ source, ...unwrapped ] as const));
        },
        syncToObject: (sources, options = {}) => {
            const name = options.name ?? `source`;
            const o = { ...sources };
            o[name] = source;
            return wrap(Ops.syncToObject(o, options));
        },
        tapProcess: (...processors) => {
            Ops.tapProcess(source, ...processors);
            return wrap(source);
        },
        tapStream: (divergedStream) => {
            Ops.tapStream(source, divergedStream);
            return wrap(source);
        },
        tapOps: (source, ...ops) => {
            Ops.tapOps(source, ...ops);
            return wrap(source);
        },
        throttle: (options = {}) => {
            return wrap(Ops.throttle(source, options));
        },
        transform: (transformer, options = {}) => {
            return wrap(Ops.transform(source, transformer, options));
        },
        timeoutValue: (options) => {
            return wrap(Ops.timeoutValue(source, options));
        },
        timeoutPing: (options) => {
            return wrap(Ops.timeoutPing(source, options));
        },
        toArray: (options) => {
            return toArray(source, options);
        },
        toArrayOrThrow: (options) => {
            return toArrayOrThrow(source, options);
        },
        onValue: (callback) => {
            const s = resolveSource(source);
            s.on(message => {
                if (messageHasValue(message))
                    callback(message.value);
            });
        }
    };
}
